import { genericMemo } from "@redotech/react-util/component";
import {
  ArrayPredicateExpression,
  ArrayPredicateOperator,
  predicateOperators,
} from "@redotech/redo-model/advanced-flow/expressions/array-expression";
import { GenericDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/generic-types";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { camelToTitleCase } from "@redotech/string/transform";
import { DeepPartial } from "@redotech/util/type";
import { useEffect, useMemo } from "react";
import { FieldInspector } from ".";

export const ArrayInspector = genericMemo(function ArrayInspector({
  schema,
  dataType,
  selectedField,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  dataType: GenericDataType;
  selectedField: Schema["fields"][string];
  expression: DeepPartial<ArrayPredicateExpression>;
  setExpression: (expression: DeepPartial<ArrayPredicateExpression>) => void;
  showValidationErrors?: boolean;
}) {
  const unwrappedDataType = dataType.replace(
    "Multiple ",
    "",
  ) as GenericDataType;

  useEffect(() => {
    if (!expression.type) {
      setExpression({ ...expression, type: "array_predicate" });
    }
    if (!expression.operator) {
      setExpression({ ...expression, operator: "some" });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  const subExpresion = expression.condition ?? {};

  // kind of a hack to make sure the field is always populated
  subExpresion.field = expression.field;

  const options: RedoListItem<ArrayPredicateOperator>[] = useMemo(() => {
    return predicateOperators.map((operator) => ({
      id: operator,
      type: "text",
      text: camelToTitleCase(operator),
      value: operator,
    }));
  }, []);

  const selectedItem = options.find((o) => o.id === expression.operator);

  return (
    <>
      <RedoSingleSelectDropdownInput<ArrayPredicateOperator>
        label="Comparison type"
        options={options}
        optionSelected={({ item }) => {
          setExpression({ ...expression, operator: item.value });
        }}
        selectedItem={selectedItem}
        size="xs"
      />

      <FieldInspector
        dataType={unwrappedDataType}
        expression={subExpresion}
        schema={schema}
        selectedField={selectedField}
        setExpression={(condition) =>
          setExpression({ ...expression, condition })
        }
        showValidationErrors={showValidationErrors}
      />
    </>
  );
});
